import { getPostHog, isPostHogInitialized } from './posthog';

type AnalyticsPrimitive = string | number | boolean | null | undefined;
type AnalyticsProperties = Record<string, AnalyticsPrimitive>;

export type AnalyticsRole = 'provider' | 'substitute' | 'operator' | 'admin' | 'unknown';
export type AuthMethod = 'password' | 'google' | 'microsoft' | 'apple' | 'sso' | 'unknown';
export type ServiceSource = 'provider_dashboard' | 'provider_services' | 'marketplace' | 'service_detail' | 'billing' | 'unknown';
export type PaymentType = 'provider_platform_fee' | 'substitute_platform_fee' | 'work_payment' | 'invoice_payment';
export type DocumentUploadSurface = 'service_documents' | 'verification_documents';
export type DurationBucket = 'under_4h' | '4_8h' | '8_12h' | 'over_12h' | 'unknown';

export interface UserIdentityProperties extends AnalyticsProperties {
  role?: AnalyticsRole;
  is_mvz?: boolean | undefined;
  onboarding_complete?: boolean | undefined;
  profile_complete?: boolean | undefined;
}

export interface AnalyticsEventProperties {
  user_signed_up: {
    role: AnalyticsRole;
    method: AuthMethod;
    intent?: string | undefined;
    has_pending_application?: boolean | undefined;
  };
  user_signed_in: {
    method: AuthMethod;
  };
  user_signed_out: Record<string, never>;
  cookie_consent_accepted: {
    analytics: true;
  };
  cookie_consent_saved: {
    analytics: true;
  };
  onboarding_completed: {
    role: AnalyticsRole;
    source: 'onboarding_dialog';
  };
  profile_updated: {
    role?: AnalyticsRole | undefined;
    section?: string | undefined;
  };
  service_created: {
    service_id?: string | undefined;
    service_type?: string | undefined;
    compensation_type?: string | undefined;
    duration_bucket?: DurationBucket | undefined;
    source: ServiceSource;
  };
  service_creation_failed: {
    service_type?: string | undefined;
    compensation_type?: string | undefined;
    duration_bucket?: DurationBucket | undefined;
    source: ServiceSource;
    error_category: string;
  };
  service_updated: {
    service_id?: string | undefined;
    source: ServiceSource;
  };
  service_deleted: {
    service_id?: string | undefined;
    source: ServiceSource;
  };
  service_viewed: {
    service_id?: string | undefined;
    service_type?: string | undefined;
    source: ServiceSource;
  };
  service_applied: {
    service_id?: string | undefined;
    application_id?: string | undefined;
    service_type?: string | undefined;
    compensation_type?: string | undefined;
    source: ServiceSource;
  };
  service_application_failed: {
    service_id?: string | undefined;
    service_type?: string | undefined;
    compensation_type?: string | undefined;
    source: ServiceSource;
    error_category: string;
  };
  chain_application_submitted: {
    chain_id?: string | undefined;
    duty_count?: number | undefined;
    source: ServiceSource;
  };
  chain_application_failed: {
    chain_id?: string | undefined;
    duty_count?: number | undefined;
    source: ServiceSource;
    error_category: string;
  };
  marketplace_filters_changed: {
    selected_state_count: number;
    has_service_type: boolean;
    has_from_date: boolean;
    has_to_date: boolean;
    sort: string;
    merkliste_only: boolean;
    view_mode: string;
  };
  payment_initiated: {
    payment_id?: string | undefined;
    payment_type: PaymentType;
    amount?: number | undefined;
    currency: string;
    source: ServiceSource;
  };
  payment_checkout_started: {
    payment_id?: string | undefined;
    payment_type: PaymentType;
    amount?: number | undefined;
    currency: string;
    source: ServiceSource;
  };
  payment_completed: {
    payment_id?: string | undefined;
    payment_type: PaymentType;
    amount?: number | undefined;
    currency: string;
    source: ServiceSource;
    mode?: 'checkout' | 'sepa_skip' | 'free' | 'status_observed' | undefined;
  };
  payment_deferred: {
    payment_id?: string | undefined;
    payment_type: PaymentType;
    amount?: number | undefined;
    currency: string;
    source: ServiceSource;
    collection_mode: 'invoice_transfer';
  };
  payment_failed: {
    payment_id?: string | undefined;
    payment_type: PaymentType;
    amount?: number | undefined;
    currency: string;
    source: ServiceSource;
    error_category: string;
  };
  document_upload_succeeded: {
    surface: DocumentUploadSurface;
    document_type?: string | undefined;
    file_count: number;
  };
  document_upload_failed: {
    surface: DocumentUploadSurface;
    document_type?: string | undefined;
    file_count: number;
    error_category: string;
  };
  notification_viewed: {
    notification_id: string;
  };
  notification_clicked: {
    notification_id: string;
  };
  page_viewed: {
    screen_name: string;
  };
  error_occurred: {
    error_message: string;
    error_name: string;
    error_stack?: string | undefined;
    context?: string | undefined;
  };
}

export type AnalyticsEventName = keyof AnalyticsEventProperties;

const compactProperties = <T extends AnalyticsProperties>(properties: T): AnalyticsProperties => {
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined)) as AnalyticsProperties;
};

export const getDurationBucket = (startDate?: number, endDate?: number): DurationBucket => {
  if (typeof startDate !== 'number' || typeof endDate !== 'number' || endDate <= startDate) {
    return 'unknown';
  }

  const hours = (endDate - startDate) / (1000 * 60 * 60);
  if (hours < 4) return 'under_4h';
  if (hours < 8) return '4_8h';
  if (hours < 12) return '8_12h';
  return 'over_12h';
};

export const normalizeAnalyticsRole = (role: unknown): AnalyticsRole => {
  if (role === 'provider' || role === 'substitute' || role === 'operator' || role === 'admin') {
    return role;
  }
  return 'unknown';
};

// Read-only: the role Clerk assigned server-side (e.g. via invitation), used only to tag analytics events.
export const getClerkUserAnalyticsRole = (user: { publicMetadata?: Record<string, unknown> } | null | undefined): AnalyticsRole =>
  normalizeAnalyticsRole(user?.publicMetadata?.['role']);

export const getAnalyticsErrorCategory = (error: unknown): string => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (message.includes('auth') || message.includes('session')) return 'auth';
    if (message.includes('permission') || message.includes('forbidden') || message.includes('unauthorized')) return 'permission';
    if (message.includes('validation') || message.includes('invalid')) return 'validation';
    if (message.includes('payment') || message.includes('mollie')) return 'payment_provider';
    if (message.includes('network') || message.includes('fetch')) return 'network';
    return error.name || 'error';
  }

  if (typeof error === 'string') {
    const lower = error.toLowerCase();
    if (lower.includes('auth') || lower.includes('session')) return 'auth';
    if (lower.includes('validation') || lower.includes('invalid')) return 'validation';
    return 'string_error';
  }

  return 'unknown';
};

/**
 * Track an analytics event.
 */
export const trackEvent = <EventName extends AnalyticsEventName>(eventName: EventName, properties: AnalyticsEventProperties[EventName]): void => {
  if (!isPostHogInitialized()) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] PostHog not initialized, skipping event:', eventName, properties);
    }
    return;
  }

  // Analytics is fire-and-forget: a failure here must never alter caller control
  // flow (e.g. leave a payment button spinning or flip a success into a failure).
  try {
    const posthog = getPostHog();
    if (posthog) {
      posthog.capture(eventName, compactProperties(properties));
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Failed to capture event:', eventName, error);
    }
  }
};

/**
 * Identify a user. Pass stable IDs and non-PII traits only.
 */
export const identifyUser = (userId: string, properties?: UserIdentityProperties): void => {
  if (!isPostHogInitialized()) {
    return;
  }

  try {
    const posthog = getPostHog();
    if (posthog) {
      posthog.identify(userId, properties ? compactProperties(properties) : undefined);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Failed to identify user:', error);
    }
  }
};

/**
 * Set user properties. Pass non-PII traits only.
 */
export const setUserProperties = (properties: UserIdentityProperties): void => {
  if (!isPostHogInitialized()) {
    return;
  }

  try {
    const posthog = getPostHog();
    if (posthog) {
      posthog.people.set(compactProperties(properties));
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Failed to set user properties:', error);
    }
  }
};

/**
 * Track page view for TanStack Router.
 */
export const trackPageView = (path: string, properties?: { route?: string; search?: string }): void => {
  if (!isPostHogInitialized()) {
    return;
  }

  try {
    const posthog = getPostHog();
    if (posthog) {
      posthog.capture(
        '$pageview',
        compactProperties({
          $current_url: window.location.href,
          path,
          route: properties?.route,
          search: properties?.search,
        })
      );
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Failed to capture pageview:', error);
    }
  }
};

/**
 * Track screen view for specific app views.
 */
export const trackScreenView = (screenName: string): void => {
  trackEvent('page_viewed', {
    screen_name: screenName,
  });
};

/**
 * Track error as analytics context. Prefer Sentry for detailed diagnostics.
 */
export const trackError = (error: Error, context?: string): void => {
  trackEvent('error_occurred', {
    error_message: error.message,
    error_name: error.name,
    error_stack: error.stack?.substring(0, 500),
    context,
  });
};

export const trackSignUpCompleted = (properties: AnalyticsEventProperties['user_signed_up']): void => {
  trackEvent('user_signed_up', properties);
};

export const trackSignInCompleted = (properties: AnalyticsEventProperties['user_signed_in']): void => {
  trackEvent('user_signed_in', properties);
};

export const trackOnboardingCompleted = (role: AnalyticsRole): void => {
  trackEvent('onboarding_completed', {
    role,
    source: 'onboarding_dialog',
  });
};

export const trackServiceCreated = (serviceId: string | undefined, properties: Omit<AnalyticsEventProperties['service_created'], 'service_id'>): void => {
  trackEvent('service_created', {
    service_id: serviceId,
    ...properties,
  });
};

export const trackServiceCreationFailed = (error: unknown, properties: Omit<AnalyticsEventProperties['service_creation_failed'], 'error_category'>): void => {
  trackEvent('service_creation_failed', {
    ...properties,
    error_category: getAnalyticsErrorCategory(error),
  });
};

export const trackMarketplaceFiltersChanged = (properties: AnalyticsEventProperties['marketplace_filters_changed']): void => {
  trackEvent('marketplace_filters_changed', properties);
};

export const trackServiceApplied = (properties: AnalyticsEventProperties['service_applied']): void => {
  trackEvent('service_applied', properties);
};

export const trackServiceApplicationFailed = (error: unknown, properties: Omit<AnalyticsEventProperties['service_application_failed'], 'error_category'>): void => {
  trackEvent('service_application_failed', {
    ...properties,
    error_category: getAnalyticsErrorCategory(error),
  });
};

export const trackChainApplicationSubmitted = (properties: AnalyticsEventProperties['chain_application_submitted']): void => {
  trackEvent('chain_application_submitted', properties);
};

export const trackChainApplicationFailed = (error: unknown, properties: Omit<AnalyticsEventProperties['chain_application_failed'], 'error_category'>): void => {
  trackEvent('chain_application_failed', {
    ...properties,
    error_category: getAnalyticsErrorCategory(error),
  });
};

export const trackPaymentInitiated = (paymentType: PaymentType, properties: Omit<AnalyticsEventProperties['payment_initiated'], 'payment_type' | 'currency'> & { currency?: string }): void => {
  trackEvent('payment_initiated', {
    payment_type: paymentType,
    ...properties,
    currency: properties.currency ?? 'EUR',
  });
};

export const trackPaymentCheckoutStarted = (paymentType: PaymentType, properties: Omit<AnalyticsEventProperties['payment_checkout_started'], 'payment_type' | 'currency'> & { currency?: string }): void => {
  trackEvent('payment_checkout_started', {
    payment_type: paymentType,
    ...properties,
    currency: properties.currency ?? 'EUR',
  });
};

export const trackPaymentCompleted = (paymentType: PaymentType, properties: Omit<AnalyticsEventProperties['payment_completed'], 'payment_type' | 'currency'> & { currency?: string }): void => {
  trackEvent('payment_completed', {
    payment_type: paymentType,
    ...properties,
    currency: properties.currency ?? 'EUR',
  });
};

export const trackPaymentDeferred = (
  paymentType: PaymentType,
  properties: Omit<AnalyticsEventProperties['payment_deferred'], 'payment_type' | 'currency'> & {
    currency?: string;
  }
): void => {
  trackEvent('payment_deferred', {
    payment_type: paymentType,
    ...properties,
    currency: properties.currency ?? 'EUR',
  });
};

export const trackPaymentFailed = (paymentType: PaymentType, error: unknown, properties: Omit<AnalyticsEventProperties['payment_failed'], 'payment_type' | 'currency' | 'error_category'> & { currency?: string }): void => {
  trackEvent('payment_failed', {
    payment_type: paymentType,
    ...properties,
    currency: properties.currency ?? 'EUR',
    error_category: getAnalyticsErrorCategory(error),
  });
};

export const trackDocumentUploadSucceeded = (properties: AnalyticsEventProperties['document_upload_succeeded']): void => {
  trackEvent('document_upload_succeeded', properties);
};

export const trackDocumentUploadFailed = (error: unknown, properties: Omit<AnalyticsEventProperties['document_upload_failed'], 'error_category'>): void => {
  trackEvent('document_upload_failed', {
    ...properties,
    error_category: getAnalyticsErrorCategory(error),
  });
};

export const trackNotificationViewed = (notificationId: string): void => {
  trackEvent('notification_viewed', {
    notification_id: notificationId,
  });
};

export const trackNotificationClicked = (notificationId: string): void => {
  trackEvent('notification_clicked', {
    notification_id: notificationId,
  });
};
